'use client';

import { c as _c } from "react/compiler-runtime";
import './child-gifts.scss';
import type { PropsWithChildren } from 'react';
import React, { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import useCollapsible from '@sola/ark-core/utils/hooks/useCollapsible.ts';
import { useRouter } from '@sola/ark-core/utils/hooks/useRouter.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { knownMyUrls } from '@sola/my-sdk/constants.ts';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import { DefaultGiftLimits } from '@sola/shared-utils/cart/gifts.ts';
import { absoluteUrlFromPath, knownSkippyUrls } from '@sola/skippy-sdk/urls.ts';
import { DepartureAlertTransition } from '../../molecules/departure-alert-transition/departure-alert-transition.tsx';
import { GiftCommitmentCard, GiftCommitmentCardSkeleton } from '../../molecules/gift-commitment-card/gift-commitment-card.tsx';
import { GiftsTotal } from '../../molecules/gifts-total/gifts-total.tsx';
import type { ChildGiftOption } from '../../types/gifts.ts';
import { GiftOptionsDialog } from '../gift-options-dialog/gift-options-dialog.tsx';
import { type RemovedGift, type SelectedGiftOptions, getCardElementKey, useChildGiftsLogic } from './child-gifts.logic.ts';
const getTranslationKeyGiftType = (giftType: string) => `my:gifts.${giftType}.section`;
interface ChildGiftsAlertProps {
  text?: React.ReactNode;
}
const StewardshipPromiseLink: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = absoluteUrlFromPath(knownSkippyUrls.financialIntegrity);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <LinkStyle purpose="neutral" weightOverride="light" inline={true}><Link href={t1}>{children}</Link></LinkStyle>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const ChildGiftsAlert: React.FC<ChildGiftsAlertProps> = t0 => {
  const $ = _c(2);
  const {
    text
  } = t0;
  if (!text) {
    return;
  }
  let t1;
  if ($[0] !== text) {
    t1 = <Alert variant="neutral" message={text} />;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface ChildGiftsDescriptionProps {
  text: string;
}
const ChildGiftsDescription: React.FC<ChildGiftsDescriptionProps> = t0 => {
  const $ = _c(2);
  const {
    text
  } = t0;
  let t1;
  if ($[0] !== text) {
    t1 = <Text size={Sizes.Small} asChild={true}><p className="body-neutral-mild">{text}</p></Text>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface ChildGiftsSectionProps extends PropsWithChildren {
  title?: string;
}
const ChildGiftsSection: React.FC<ChildGiftsSectionProps> = t0 => {
  const $ = _c(21);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      defaultExpanded: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    triggerProps,
    targetProps
  } = useCollapsible(t1);
  let contentTargetProps;
  let targetClasses;
  let targetHidden;
  if ($[1] !== targetProps) {
    ({
      className: targetClasses,
      hide: targetHidden,
      ...contentTargetProps
    } = targetProps);
    $[1] = targetProps;
    $[2] = contentTargetProps;
    $[3] = targetClasses;
    $[4] = targetHidden;
  } else {
    contentTargetProps = $[2];
    targetClasses = $[3];
    targetHidden = $[4];
  }
  let t2;
  if ($[5] !== title) {
    t2 = title && <Text variant={Variants.Heading} size={Sizes.Small} asChild={true}><h3 className="ChildGiftsInfoSection__title heading-neutral-potent">{title}</h3></Text>;
    $[5] = title;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = targetHidden ? "180deg" : "0deg";
  let t4;
  if ($[7] !== t3) {
    t4 = <ArkIcon iconName="chevron-up" className="ChildGiftsInfoSection__chevron shrink-0" size="1.5rem" style={{
      rotate: t3
    }} />;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4 || $[11] !== triggerProps) {
    t5 = <button type="button" className="ChildGiftsInfoSection__header flex items-center justify-between gap-comfy-xs md:gap-comfy-sm w-full py-snug-lg" {...triggerProps}>{t2}{t4}</button>;
    $[9] = t2;
    $[10] = t4;
    $[11] = triggerProps;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== children || $[14] !== contentTargetProps || $[15] !== targetClasses || $[16] !== targetHidden) {
    t6 = !targetHidden && <div className={cn("flex flex-col gap-comfy-xl", targetClasses)} {...contentTargetProps}>{children}</div>;
    $[13] = children;
    $[14] = contentTargetProps;
    $[15] = targetClasses;
    $[16] = targetHidden;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <section>{t5}{t6}</section>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
interface ChildGiftsCommitmentListProps {
  giftType: GiftTypeEnum;
  childGiftOptions: ChildGiftOption[];
  isLoading: boolean;
  onGiftOptionsOpen: (selectedGiftOptions: SelectedGiftOptions) => void;
  onGiftRemove: (gift: RemovedGift) => Promise<void>;
}
const ChildGiftsCommitmentList: React.FC<ChildGiftsCommitmentListProps> = t0 => {
  const $ = _c(12);
  const {
    giftType,
    childGiftOptions,
    isLoading,
    onGiftOptionsOpen,
    onGiftRemove
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex flex-col gap-comfy-xs"><GiftCommitmentCardSkeleton /><GiftCommitmentCardSkeleton /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (childGiftOptions.length === 0) {
    return null;
  }
  let t1;
  if ($[1] !== childGiftOptions || $[2] !== giftType || $[3] !== onGiftOptionsOpen || $[4] !== onGiftRemove) {
    let t2;
    if ($[6] !== giftType || $[7] !== onGiftOptionsOpen || $[8] !== onGiftRemove) {
      t2 = t3 => {
        const {
          commitment,
          giftOption
        } = t3;
        const cardElementKey = getCardElementKey(commitment.participant?.globalId ?? "", giftType);
        return <div key={cardElementKey} id={cardElementKey}><GiftCommitmentCard commitment={commitment} giftType={giftType} giftOption={giftOption} onGiftOptionsOpen={returnFocusRef => onGiftOptionsOpen({
            commitment,
            giftOption,
            giftType,
            returnFocusRef
          })} onGiftRemove={onGiftRemove} /></div>;
      };
      $[6] = giftType;
      $[7] = onGiftOptionsOpen;
      $[8] = onGiftRemove;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    t1 = childGiftOptions.map(t2);
    $[1] = childGiftOptions;
    $[2] = giftType;
    $[3] = onGiftOptionsOpen;
    $[4] = onGiftRemove;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[10] !== t1) {
    t2 = <div className="flex flex-col gap-comfy-xs">{t1}</div>;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
const GiftsSkeleton: React.FC = () => {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div><Skeleton height={34} className="mb-comfy-xs" /><Skeleton height={24} /></div>;
    t1 = <Skeleton height={64} />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-col gap-comfy-xl">{t0}{t1}<div className="flex flex-col gap-comfy-xs"><GiftCommitmentCardSkeleton /><GiftCommitmentCardSkeleton /></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

/**
 * Displays a list of options for giving gifts to commitments.
 */
const ChildGiftsContent: React.FC = () => {
  const $ = _c(73);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    router
  } = useRouter();
  const {
    isGiftListLoading,
    isError,
    hasCommitments,
    hasOnlyCorrespondents,
    childGiftOptions,
    shouldShowFinalGiftSection,
    shouldShowOtherGiftSections,
    activeSelection,
    selectedGiftTypeLimits,
    showAddGiftToAllEligible,
    handleGiftOptionsOpen,
    handleGiftOptionsSubmit,
    handleGiftRemove,
    handleGiftOptionsOpenChange
  } = useChildGiftsLogic();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getTranslationKeyGiftType("birthday");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const birthdayTranslationKey = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getTranslationKeyGiftType("child");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const childTranslationKey = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getTranslationKeyGiftType("family");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const familyTranslationKey = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = getTranslationKeyGiftType("center");
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const centerTranslationKey = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = getTranslationKeyGiftType(GiftTypeEnum.Final.toLowerCase());
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const finalTranslationKey = t5;
  let t6;
  let t7;
  if ($[6] !== hasCommitments || $[7] !== isError || $[8] !== isGiftListLoading || $[9] !== router) {
    t6 = () => {
      if (!isGiftListLoading && !isError && !hasCommitments) {
        router.replace(knownMyUrls.sponsorshipsNew);
      }
    };
    t7 = [isGiftListLoading, isError, hasCommitments, router];
    $[6] = hasCommitments;
    $[7] = isError;
    $[8] = isGiftListLoading;
    $[9] = router;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  if (isError) {
    let t8;
    if ($[12] !== t) {
      t8 = t("my:gifts.requestError.title");
      $[12] = t;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== t) {
      t9 = t("my:gifts.requestError.message");
      $[14] = t;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    let t10;
    if ($[16] !== t) {
      t10 = t("my:ctas.contactSupport.url");
      $[16] = t;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    let t11;
    if ($[18] !== t) {
      t11 = t("my:ctas.contactSupport.text");
      $[18] = t;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    let t12;
    if ($[20] !== t10 || $[21] !== t11) {
      t12 = <Link href={t10}>{t11}</Link>;
      $[20] = t10;
      $[21] = t11;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    let t13;
    if ($[23] !== t12 || $[24] !== t8 || $[25] !== t9) {
      t13 = <Alert title={t8} message={t9} variant="negative" action={t12} />;
      $[23] = t12;
      $[24] = t8;
      $[25] = t9;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    return t13;
  }
  if (isGiftListLoading || !hasCommitments) {
    let t8;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <GiftsSkeleton />;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    return t8;
  }
  if (hasOnlyCorrespondents) {
    let t8;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <DepartureAlertTransition />;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    let t9;
    if ($[29] !== t) {
      t9 = t("my:gifts.correspondentOnly.title");
      $[29] = t;
      $[30] = t9;
    } else {
      t9 = $[30];
    }
    let t10;
    if ($[31] !== t) {
      t10 = t("core:contact.phonePrimary");
      $[31] = t;
      $[32] = t10;
    } else {
      t10 = $[32];
    }
    const t11 = `tel:${t10}`;
    let t12;
    if ($[33] !== t) {
      t12 = t("core:contact.phonePrimary");
      $[33] = t;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    let t13;
    if ($[35] !== t11 || $[36] !== t12) {
      t13 = <IntlBlock i18nKey="my:gifts.correspondentOnly.message" components={{
        SupportPhone: <Link href={t11} target="_self">{t12}</Link>
      }} />;
      $[35] = t11;
      $[36] = t12;
      $[37] = t13;
    } else {
      t13 = $[37];
    }
    let t14;
    if ($[38] !== t13 || $[39] !== t9) {
      t14 = <>{t8}<Alert title={t9} message={t13} variant="neutral" /></>;
      $[38] = t13;
      $[39] = t9;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    return t14;
  }
  let t8;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DepartureAlertTransition />;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== childGiftOptions?.Final || $[43] !== handleGiftOptionsOpen || $[44] !== handleGiftRemove || $[45] !== isGiftListLoading || $[46] !== shouldShowFinalGiftSection || $[47] !== t) {
    t9 = shouldShowFinalGiftSection && <><ChildGiftsSection title={t(`${finalTranslationKey}.title`)}><ChildGiftsDescription text={t(`${finalTranslationKey}.message`)} /><ChildGiftsAlert text={<IntlBlock i18nKey={`${finalTranslationKey}.alertText`} values={{
          min: t("core:numbers.currencyCustom", {
            amount: DefaultGiftLimits[GiftTypeEnum.Final].min
          }),
          max: t("core:numbers.currencyCustom", {
            amount: DefaultGiftLimits[GiftTypeEnum.Final].max
          })
        }} />} /><ChildGiftsCommitmentList giftType={GiftTypeEnum.Final} childGiftOptions={childGiftOptions?.Final ?? []} isLoading={isGiftListLoading} onGiftOptionsOpen={handleGiftOptionsOpen} onGiftRemove={handleGiftRemove} /></ChildGiftsSection><Divider className="my-comfy-xl" purpose="neutral" /></>;
    $[42] = childGiftOptions?.Final;
    $[43] = handleGiftOptionsOpen;
    $[44] = handleGiftRemove;
    $[45] = isGiftListLoading;
    $[46] = shouldShowFinalGiftSection;
    $[47] = t;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  let t10;
  if ($[49] !== childGiftOptions?.Birthday || $[50] !== childGiftOptions?.Center || $[51] !== childGiftOptions?.Child || $[52] !== childGiftOptions?.Family || $[53] !== handleGiftOptionsOpen || $[54] !== handleGiftRemove || $[55] !== isGiftListLoading || $[56] !== shouldShowOtherGiftSections || $[57] !== t) {
    t10 = shouldShowOtherGiftSections && <><ChildGiftsSection title={t(`${birthdayTranslationKey}.title`)}><ChildGiftsDescription text={t(`${birthdayTranslationKey}.message`)} /><ChildGiftsAlert text={<IntlBlock i18nKey={`${birthdayTranslationKey}.alertText`} values={{
          min: t("core:numbers.currencyCustom", {
            amount: DefaultGiftLimits[GiftTypeEnum.Birthday].min
          }),
          max: t("core:numbers.currencyCustom", {
            amount: DefaultGiftLimits[GiftTypeEnum.Birthday].max
          })
        }} />} /><ChildGiftsCommitmentList giftType={GiftTypeEnum.Birthday} childGiftOptions={childGiftOptions?.Birthday ?? []} isLoading={isGiftListLoading} onGiftOptionsOpen={handleGiftOptionsOpen} onGiftRemove={handleGiftRemove} /></ChildGiftsSection><Divider className="my-comfy-xl" purpose="neutral" /><ChildGiftsSection title={t(`${childTranslationKey}.title`)}><ChildGiftsDescription text={t(`${childTranslationKey}.message`)} /><ChildGiftsAlert text={<IntlBlock i18nKey={`${childTranslationKey}.alertText`} values={{
          min: t("core:numbers.currencyCustom", {
            amount: DefaultGiftLimits[GiftTypeEnum.Child].min
          }),
          max: t("core:numbers.currencyCustom", {
            amount: DefaultGiftLimits[GiftTypeEnum.Child].max
          })
        }} />} /><ChildGiftsCommitmentList giftType={GiftTypeEnum.Child} childGiftOptions={childGiftOptions?.Child ?? []} isLoading={isGiftListLoading} onGiftOptionsOpen={handleGiftOptionsOpen} onGiftRemove={handleGiftRemove} /></ChildGiftsSection><Divider className="my-comfy-xl" purpose="neutral" /><ChildGiftsSection title={t(`${familyTranslationKey}.title`)}><ChildGiftsDescription text={t(`${familyTranslationKey}.message`)} /><ChildGiftsAlert text={<IntlBlock i18nKey={`${familyTranslationKey}.alertText`} values={{
          min: t("core:numbers.currencyCustom", {
            amount: DefaultGiftLimits[GiftTypeEnum.Family].min
          }),
          max: t("core:numbers.currencyCustom", {
            amount: DefaultGiftLimits[GiftTypeEnum.Family].max
          })
        }} />} /><ChildGiftsCommitmentList giftType={GiftTypeEnum.Family} childGiftOptions={childGiftOptions?.Family ?? []} isLoading={isGiftListLoading} onGiftOptionsOpen={handleGiftOptionsOpen} onGiftRemove={handleGiftRemove} /></ChildGiftsSection><Divider className="my-comfy-xl" purpose="neutral" /><ChildGiftsSection title={t(`${centerTranslationKey}.title`)}><ChildGiftsDescription text={t(`${centerTranslationKey}.message`)} /><ChildGiftsAlert text={<IntlBlock i18nKey={`${centerTranslationKey}.alertText`} values={{
          giftsMax: 4,
          amountMax: t("core:numbers.currencyCustom", {
            amount: DefaultGiftLimits[GiftTypeEnum.Center].max
          })
        }} />} /><ChildGiftsCommitmentList giftType={GiftTypeEnum.Center} childGiftOptions={childGiftOptions?.Center ?? []} isLoading={isGiftListLoading} onGiftOptionsOpen={handleGiftOptionsOpen} onGiftRemove={handleGiftRemove} /></ChildGiftsSection><Divider className="my-comfy-xl" purpose="neutral" /></>;
    $[49] = childGiftOptions?.Birthday;
    $[50] = childGiftOptions?.Center;
    $[51] = childGiftOptions?.Child;
    $[52] = childGiftOptions?.Family;
    $[53] = handleGiftOptionsOpen;
    $[54] = handleGiftRemove;
    $[55] = isGiftListLoading;
    $[56] = shouldShowOtherGiftSections;
    $[57] = t;
    $[58] = t10;
  } else {
    t10 = $[58];
  }
  let t11;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <GiftsTotal />;
    $[59] = t11;
  } else {
    t11 = $[59];
  }
  let t12;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Text variant={Variants.Label} asChild={true}><p className="body-neutral-mild mt-comfy-xl"><IntlBlock i18nKey="my:gifts.giftGivingDisclaimer" components={{
          Link: <StewardshipPromiseLink />
        }} /></p></Text>;
    $[60] = t12;
  } else {
    t12 = $[60];
  }
  let t13;
  if ($[61] !== t10 || $[62] !== t9) {
    t13 = <div className="GiftGiving mt-comfy-xl">{t9}{t10}{t11}{t12}</div>;
    $[61] = t10;
    $[62] = t9;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== activeSelection || $[65] !== handleGiftOptionsOpenChange || $[66] !== handleGiftOptionsSubmit || $[67] !== selectedGiftTypeLimits || $[68] !== showAddGiftToAllEligible) {
    t14 = activeSelection && <GiftOptionsDialog commitment={activeSelection.commitment} giftType={activeSelection.giftType} giftTypeLimits={selectedGiftTypeLimits} showAddGiftToAllEligible={showAddGiftToAllEligible} initialAmount={activeSelection.giftOption.cartItem?.amount} initialFrequency={activeSelection.giftOption.cartItem?.frequency} buttonAction={activeSelection.giftOption.buttonAction ?? undefined} open={true} onOpenChange={handleGiftOptionsOpenChange} onSubmit={handleGiftOptionsSubmit} />;
    $[64] = activeSelection;
    $[65] = handleGiftOptionsOpenChange;
    $[66] = handleGiftOptionsSubmit;
    $[67] = selectedGiftTypeLimits;
    $[68] = showAddGiftToAllEligible;
    $[69] = t14;
  } else {
    t14 = $[69];
  }
  let t15;
  if ($[70] !== t13 || $[71] !== t14) {
    t15 = <>{t8}{t13}{t14}</>;
    $[70] = t13;
    $[71] = t14;
    $[72] = t15;
  } else {
    t15 = $[72];
  }
  return t15;
};
export const ChildGifts: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<GiftsSkeleton />}><ChildGiftsContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};